import { types, cast } from "mobx-state-tree";
export const UIDocumentGroup = types
    .model("UIDocumentGroup", {
    id: types.identifier,
    /**
     * Either undefined, an empty array, or an array of document ids.
     * - Undefined means the user hasn't chosen anything yet so we can show a default view
     *   in some cases this might be showing the first document of the listing.
     * - Empty array means the user has explicitly chosen to close the documents and
     *   this typically means they'll be viewing the listing of documents.
     * - The array typically has a single document id, in some cases a second document
     *   can be open at the same time.
     *
     * TODO: another approach for handling the "user hasn't chosen anything yet" case is
     * to look to see if there is a document group at all. Currently the visitedDocumentGroup
     * isn't created until a user actually opens a document. But before making that switch
     * we need to have a working example of this "automaticallyOpenFirstDocument" setting.
     */
    currentDocumentKeys: types.maybe(types.array(types.string))
})
    .views(self => ({
    get primaryDocumentKey() {
        if (!self.currentDocumentKeys || self.currentDocumentKeys.length < 1) {
            return undefined;
        }
        return self.currentDocumentKeys[0];
    },
    get secondaryDocumentKey() {
        if (!self.currentDocumentKeys || self.currentDocumentKeys.length < 2) {
            return undefined;
        }
        return self.currentDocumentKeys[1];
    },
    get userExplicitlyClosedDocument() {
        var _a;
        return ((_a = self.currentDocumentKeys) === null || _a === void 0 ? void 0 : _a.length) === 0;
    }
}))
    .actions(self => ({
    setPrimaryDocumentKey(documentKey) {
        let docKeys = self.currentDocumentKeys;
        if (!docKeys) {
            self.currentDocumentKeys = cast([]);
            docKeys = self.currentDocumentKeys;
        }
        if (docKeys.length === 0) {
            docKeys.push(documentKey);
        }
        else if (docKeys.length > 0) {
            docKeys[0] = documentKey;
        }
    },
    /**
     * If there is no primary document we just set the documentKey as the primary document
     * and print a warning in the console
     * @param documentKey
     */
    setSecondaryDocumentKey(documentKey) {
        let docKeys = self.currentDocumentKeys;
        if (!docKeys) {
            self.currentDocumentKeys = cast([]);
            docKeys = self.currentDocumentKeys;
        }
        if (docKeys.length === 0) {
            console.warn("setting a secondary document when there is no primary document");
            docKeys.push(documentKey);
        }
        else if (docKeys.length === 1) {
            docKeys.push(documentKey);
        }
        else if (docKeys.length > 1) {
            docKeys[1] = documentKey;
        }
    },
    /**
     * Close the current primary document. If there is a secondary document it will become
     * the primary document.
     * @returns
     */
    closePrimaryDocument() {
        const docKeys = self.currentDocumentKeys;
        if (!docKeys) {
            // The user is taking the explicit action to close the document
            // So we save that as an empty array.
            self.currentDocumentKeys = cast([]);
            return;
        }
        if (docKeys.length === 0)
            return;
        docKeys.splice(0, 1);
    },
    /**
     *
     * @returns
     */
    closeSecondaryDocument() {
        const docKeys = self.currentDocumentKeys;
        if (!docKeys || docKeys.length < 2)
            return;
        docKeys.splice(1, 1);
    }
}));
//# sourceMappingURL=data:application/json;base64,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